/* eslint-disable max-lines */
import { reject, pick } from 'ramda';
import * as types from '../mutation-types';
import { apolloClient } from '@/apollo';
import {
  CreateOrUpdateCodeInput,
  CreateOrUpdateQuotationsDocument,
  CreateOrUpdateQuotationsMutation,
  CreateOrUpdateQuotationsMutationVariables,
  DeleteCodeDocument,
  DeleteCodeMutation,
  DeleteCodeMutationVariables,
  RestoreCodeDocument,
  RestoreCodeMutation,
  RestoreCodeMutationVariables,
  UpdateCodeDocument,
  UpdateCodeMutation,
  UpdateCodeMutationVariables,
  AddCodeToGroupDocument,
  RemoveCodeFromGroupDocument,
  DuplicateCodeDocument,
  CreateCodeDocument,
  MergeCodesDocument,
  CodeGroupsFragmentDoc,
  CodeDefaultFragmentDoc,
  CreateCodeMutation,
  CreateCodeMutationVariables,
  CodeGroupsFragment,
  DuplicateCodeMutation,
  DuplicateCodeMutationVariables,
} from '@/graphql/operations.gen';
import updateProjectCache from '@utils/updateProjectCache';
import { getTracker } from '@/tracking/trackingPlugin';
import { CodeEvent, TrackingProperty } from '@/tracking/trackingTypes';
import { v4 as uuid } from 'uuid';
import { getCachedGroup } from '@utils/getCachedGroup';
import { updateAiCodeSuggestionInCache, updateGroupContentsInCache } from '@utils/updateEntitiesInCache';
import { updateRecentCodes } from '@utils/updateRecentCodes';
import { ProjectCode, ProjectQuotation } from '@/types';
import { ApolloCache } from '@apollo/client/core';
import { Action } from 'vuex';
import { RootState } from '../types';

export type QuotationCodeAttachments = Map<string, ProjectQuotation['codes']>;

const updateCodeInCache = (
  store: ApolloCache<any>,
  { id },
  updateFn: (code: CodeGroupsFragment) => CodeGroupsFragment
) => {
  const cacheId = `Code:${id}`;

  const cacheData = store.readFragment<CodeGroupsFragment>({
    fragment: CodeGroupsFragmentDoc,
    fragmentName: 'CodeGroups',
    id: cacheId,
  });

  if (cacheData) {
    store.writeFragment<CodeGroupsFragment>({
      fragment: CodeGroupsFragmentDoc,
      fragmentName: 'CodeGroups',
      data: updateFn(cacheData),
      id: cacheId,
    });
  }
};

// initial state
const state = {};

// getters
const getters = {};

// actions
const actions: Record<string, Action<{}, RootState>> = {
  /**
   * Create code.
   *
   * @param {*} context
   * @param {*} payload
   */
  create({ rootGetters, commit }, data) {
    commit(types.CODE_CREATE, {
      meta: {
        analytics: [['event', 'code', 'create']],
      },
    });

    return apolloClient
      .mutate<CreateCodeMutation, CreateCodeMutationVariables>({
        mutation: CreateCodeDocument,
        variables: {
          ...data,
        },
        // placeholder comes from CodeSelect / QuotationUpsert
        // it's similar to an optimistic code,
        // and an actual optimistic code would lead to visual duplicates
        optimisticResponse: data.placeholder
          ? null
          : {
              __typename: 'Mutation',
              createCode: {
                __typename: 'Code',
                id: data.id || `optimistic:${uuid()}`,
                name: data.name,
                fullname: data.name,
                comment: data.comment || '',
                color: data.color,
                createdAt: new Date().toISOString(),
                createdBy: rootGetters['user/activityData'],
                updatedAt: new Date().toISOString(),
                updatedBy: rootGetters['user/activityData'],
                groups: [],
                // @ts-expect-error
                placeholder: true,
                folder: null,
                isCategory: false,
                parent: null,
              },
            },
        update(store, response) {
          /**
           * Add occurrence to fix typing - it is actually computed client side by apollo
           */
          const codeWithOccurrence = { ...response.data.createCode, occurrence: 0 };

          updateRecentCodes(data.projectId, [response.data.createCode.id]);
          updateProjectCache(store, data.projectId, (project) => {
            let codeGroups = project.codeGroups;

            if (data.groups) {
              codeGroups = [...project.codeGroups];

              data.groups.forEach((groupId) => {
                const codeGroupIndex = codeGroups.findIndex((group) => group.id === groupId);

                codeGroups[codeGroupIndex] = {
                  ...codeGroups[codeGroupIndex],
                  contents: [...codeGroups[codeGroupIndex].contents, codeWithOccurrence],
                };
              });
            }

            return {
              codes: [...project.codes, codeWithOccurrence],
              codeGroups,
            };
          });

          updateAiCodeSuggestionInCache(store, data, codeWithOccurrence);

          return response;
        },
      })
      .then((response) => {
        const code = response.data.createCode;
        getTracker().track(CodeEvent.CodeCreated, {
          [TrackingProperty.HasComment]: Boolean(code.comment),
          [TrackingProperty.Color]: code.color,
        });

        return response.data.createCode;
      });
  },

  /**
   * Create multiple codes and their possible parents
   *
   * Borrows CreateOrUpdateQuotations mutation
   *
   */
  async createWithParents(
    {},
    data: {
      projectId: string;
      codes: Array<CreateOrUpdateCodeInput>;
      parentCodes: Array<CreateOrUpdateCodeInput>;
    }
  ) {
    const result = await apolloClient.mutate<
      CreateOrUpdateQuotationsMutation,
      CreateOrUpdateQuotationsMutationVariables
    >({
      mutation: CreateOrUpdateQuotationsDocument,
      variables: {
        projectId: data.projectId,
        codes: data.codes.map((code) => ({
          ...pick(['id', 'name', 'isNewCode', 'color', 'comment'], code),
          parent: pick(['id', 'name'], code.parent),
        })),
        parentCodes: data.parentCodes.map((parentCode) =>
          pick(['id', 'name', 'isNewCode', 'color', 'comment'], parentCode)
        ),
        quotations: [],
      },
      update(store, response) {
        const newOrReusedCodes = [
          ...response.data.createOrUpdateQuotations.codes,
          ...response.data.createOrUpdateQuotations.parentCodes,
        ].map((code) => ({ ...code, occurrence: 0 }));

        updateRecentCodes(
          data.projectId,
          response.data.createOrUpdateQuotations.codes.map((code) => code.id)
        );
        updateProjectCache(store, data.projectId, (project) => ({
          codes: [...project.codes, ...newOrReusedCodes],
        }));

        [...data.codes, ...data.parentCodes].forEach((code) => {
          // CreateOrUpdateQuotations is guaranteed to match the name without fuzzing,
          // so we can find the actual code by name match
          const matchingCode = newOrReusedCodes.find(
            (newCode) => newCode.name.toLowerCase() === code.name.toLowerCase()
          );

          updateAiCodeSuggestionInCache(store, code, matchingCode);
        });

        return response;
      },
    });

    result.data.createOrUpdateQuotations.codes.forEach((code) => {
      getTracker().track(CodeEvent.CodeCreated, {
        [TrackingProperty.HasComment]: Boolean(code.comment),
        [TrackingProperty.Color]: code.color,
      });
    });

    return result.data.createOrUpdateQuotations;
  },

  /**
   * Delete code.
   *
   * @param {*} context
   * @param {*} payload
   */
  delete(
    {},
    { id, projectId, moveChildrenToRoot = false }: { id: string; projectId: string; moveChildrenToRoot: boolean }
  ) {
    /**
     * Create a map of quotation code attachments that have been removed, so we can
     * restore them if necessary
     */
    const quotationCodeAttachments: QuotationCodeAttachments = new Map();

    return apolloClient
      .mutate<DeleteCodeMutation, DeleteCodeMutationVariables>({
        mutation: DeleteCodeDocument,
        variables: {
          id,
          options: {
            moveChildrenToRoot,
          },
        },
        update(store, response) {
          const deletedCodeIds: Array<string> = [
            id,
            ...response.data.deleteCode.deletedChildren.map((child) => child.id),
          ];

          updateProjectCache(store, projectId, (project) => ({
            codes: reject((code) => deletedCodeIds.includes(code.id), project.codes),
            sources: project.sources.map((source) => ({
              ...source,
              quotations: source.quotations.map((quotation) => {
                const deletedAttachments: ProjectQuotation['codes'] = [];
                const keptAttachments: ProjectQuotation['codes'] = [];

                quotation.codes.forEach((codeAttach) => {
                  if (deletedCodeIds.includes(codeAttach.code.id)) {
                    deletedAttachments.push(codeAttach);
                  } else {
                    keptAttachments.push(codeAttach);
                  }
                });

                quotationCodeAttachments.set(quotation.id, deletedAttachments);

                if (keptAttachments.length) {
                  return { ...quotation, codes: keptAttachments };
                }

                return quotation;
              }),
            })),
          }));
        },
      })
      .then((response) => ({
        deleteCode: response.data.deleteCode,
        quotationCodeAttachments,
      }));
  },

  /**
   * Restore code.
   *
   * @param {*} context
   * @param {*} payload
   */
  restore(
    {},
    {
      id,
      projectId,
      quotationCodeAttachments,
      movedChildren,
    }: {
      id: string;
      projectId: string;
      quotationCodeAttachments: QuotationCodeAttachments;
      /**
       * The children that were moved to root when the code to restore was deleted
       * @type {Array<ProjectCode>}
       */
      movedChildren: Array<ProjectCode>;
    }
  ) {
    return apolloClient.mutate<RestoreCodeMutation, RestoreCodeMutationVariables>({
      mutation: RestoreCodeDocument,
      variables: {
        id,
        childrenToAdd: movedChildren.map((code) => code.id),
      },
      update(store, response) {
        updateProjectCache(store, projectId, (project) => ({
          codes: [...project.codes, response.data.restoreCode.code, ...response.data.restoreCode.restoredChildren],
        }));

        quotationCodeAttachments.forEach((attachments, quotationId) => {
          store.modify({
            id: `Quotation:${quotationId}`,
            fields: {
              codes(value) {
                return [...value, ...attachments];
              },
            },
          });
        });

        return response.data.restoreCode.code;
      },
    });
  },

  merge({}, data) {
    return apolloClient.mutate({
      mutation: MergeCodesDocument,
      variables: data,
      refetchQueries: ['Project'], // TODO: well here we need lots of info basically, all occurrences etc. but what if we compute that in the fe
    });
  },

  /**
   * Update code.
   *
   * @param {*} context
   * @param {*} payload
   */
  update({ rootGetters }, data) {
    const cachedCode = apolloClient.readFragment({
      id: `Code:${data.id}`,
      fragment: CodeDefaultFragmentDoc,
      fragmentName: 'CodeDefault',
    });

    return apolloClient.mutate<UpdateCodeMutation, UpdateCodeMutationVariables>({
      mutation: UpdateCodeDocument,
      variables: {
        ...data,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCode: {
          code: {
            __typename: 'Code',
            ...cachedCode,
            ...data,
            updatedAt: new Date().toISOString(),
            updatedBy: rootGetters['user/activityData'],
          },
          children: [],
        },
      },
    });
  },

  addToGroup(currentState, { id, group, placeholder }) {
    const cachedGroup = getCachedGroup(apolloClient, group.id);

    return apolloClient.mutate({
      mutation: AddCodeToGroupDocument,
      variables: {
        id,
        groupId: group.id,
      },
      optimisticResponse: placeholder
        ? null
        : {
            __typename: 'Mutation',
            addToGroup: {
              id: group.id,
              name: group.name,
              __typename: 'Group',
            },
          },
      update(store, response) {
        const responseData = response.data.addToGroup;

        updateCodeInCache(store, { id }, (code) => {
          if (!code.groups.find((sGroup) => sGroup.id === group.id)) {
            return {
              groups: [...code.groups, { ...cachedGroup, ...responseData }],
            };
          }

          return {
            groups: code.groups,
          };
        });

        const code = store.readFragment({
          id: `Code:${id}`,
          fragment: CodeDefaultFragmentDoc,
          fragmentName: 'CodeDefault',
        });

        updateGroupContentsInCache(store, group.id, (data) => ({
          contents: [...data.contents, code],
        }));

        return responseData;
      },
    });
  },

  removeFromGroup(currentState, { id, group }) {
    return apolloClient.mutate({
      mutation: RemoveCodeFromGroupDocument,
      variables: {
        id,
        groupId: group.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeFromGroup: {
          id: group.id,
          name: group.name,
          __typename: 'Group',
        },
      },
      update(store, response) {
        const responseData = response.data.removeFromGroup;

        updateCodeInCache(store, { id }, (code) => ({
          groups: reject((sGroup) => sGroup.id === responseData.id, code.groups),
        }));

        updateGroupContentsInCache(store, group.id, (data) => ({
          contents: data.contents.filter((codeInGroup) => codeInGroup.id !== id),
        }));

        return responseData;
      },
    });
  },

  /**
   * Duplicate code.
   *
   * @param {*} context
   * @param {*} payload
   */
  duplicate({ commit }, { id, projectId }) {
    commit(types.CODE_DUPLICATE, {
      meta: {
        analytics: [['event', 'code', 'duplicate']],
      },
    });

    return apolloClient
      .mutate<DuplicateCodeMutation, DuplicateCodeMutationVariables>({
        mutation: DuplicateCodeDocument,
        variables: {
          id,
        },
        update(store, response) {
          updateProjectCache(store, projectId, (project) => ({
            codes: [
              ...project.codes,
              ...[response.data.duplicateCode.code, ...response.data.duplicateCode.children].map((code) => ({
                ...code,
                occurrence: 0,
              })),
            ],
          }));

          return response;
        },
      })
      .then((data) => data.data.duplicateCode);
  },
};

// mutations
const mutations = {
  [types.CODE_CREATE]() {},

  [types.CODE_DUPLICATE]() {},
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
